import { useCallback, useMemo, useState } from 'react';

import { NetworkStatus } from '@apollo/client';
import { GridSortModel } from '@mui/x-data-grid';
import { GridFilterModel } from '@mui/x-data-grid-pro';

import { useQuery } from '@redwoodjs/web';

import { PaginationMetadata, UsePaginatedQueryConfig, UsePaginatedQueryResult } from './type';

/**
 * Generic hook for paginated, sortable, and filterable GraphQL queries.
 * Integrates with Apollo Client cache and provides minimal API for NPIDataGrid.
 *
 * @param config Configuration object containing query, variables, pagination, sorting, and filters
 * @returns Object with data, loading state, error, pagination metadata, and control functions
 */
function usePaginatedQuery<T = unknown>(
    config: UsePaginatedQueryConfig
): UsePaginatedQueryResult<T> {
    const [currentPage, setCurrentPage] = useState(config.pagination?.page ?? 1);
    const [currentPageSize, setCurrentPageSize] = useState(config.pagination?.pageSize ?? 50);
    const [currentSorting, setCurrentSorting] = useState<GridSortModel>(config.sorting ?? []);
    const [currentFilters, setCurrentFilters] = useState<GridFilterModel>(
        config.filterModel ?? { items: [] }
    );

    // Build query variables by merging user variables with pagination/sorting/filtering state
    const queryVariables = useMemo(() => {
        const baseVariables = config.variables ?? {};

        const filterVars = {
            filters: {
                ...config.filters,
                filterRules: currentFilters,
                page: currentPage,
                perPage: currentPageSize,
                sortByRules: currentSorting,
            },
        };

        return {
            ...baseVariables,
            ...filterVars,
        };
    }, [
        config.variables,
        config.filters,
        currentFilters,
        currentPage,
        currentPageSize,
        currentSorting,
    ]);

    const {
        data: rawData,
        loading,
        error,
        refetch,
        networkStatus,
    } = useQuery(config.query, {
        variables: queryVariables,
        fetchPolicy: config.fetchPolicy ?? 'cache-first',
        notifyOnNetworkStatusChange: config.notifyOnNetworkStatusChange ?? true,
    });

    const { data, paginationInfo } = useMemo(() => {
        if (!rawData) {
            return { data: [], paginationInfo: null };
        }

        // Try to find the first property that contains data array and pagination info
        const responseKey = Object.keys(rawData).find((key) => {
            return (
                rawData[key] &&
                (Array.isArray(rawData[key]) ||
                    (typeof rawData[key] === 'object' && rawData[key].data))
            );
        });

        if (!responseKey) {
            return { data: [], paginationInfo: null };
        }

        const responseData = rawData[responseKey];

        if (Array.isArray(responseData)) {
            return {
                data: responseData,
                paginationInfo: {
                    total: responseData.length,
                    page: currentPage,
                    pageSize: currentPageSize,
                },
            };
        } else if (responseData && typeof responseData === 'object') {
            return {
                data: responseData.data ?? [],
                paginationInfo: {
                    total: responseData.total ?? responseData.totalCount ?? 0,
                    page: responseData.page ?? currentPage,
                    pageSize: responseData.pageSize ?? currentPageSize,
                },
            };
        }

        return { data: [], paginationInfo: null };
    }, [rawData, currentPage, currentPageSize]);

    const paginationMetadata: PaginationMetadata = useMemo(() => {
        const total = paginationInfo?.total ?? 0;
        const totalPages = Math.ceil(total / currentPageSize);

        return {
            page: currentPage,
            pageSize: currentPageSize,
            total,
            hasNextPage: currentPage < totalPages,
            hasPreviousPage: currentPage > 1,
        };
    }, [paginationInfo, currentPage, currentPageSize]);

    const fetchMore = useCallback(
        async ({ page, pageSize }: { page: number; pageSize?: number }) => {
            const newPageSize = pageSize ?? currentPageSize;

            setCurrentPage(page);
            if (pageSize !== undefined) {
                setCurrentPageSize(newPageSize);
            }

            return refetch({
                filters: {
                    ...config.filters,
                    page,
                    perPage: newPageSize,
                    sortByRules: currentSorting,
                    filterRules: currentFilters,
                },
            });
        },
        [currentPageSize, refetch, config.filters, currentSorting, currentFilters]
    );

    const updateSorting = useCallback(
        (sortModel: GridSortModel) => {
            const sortConfig: GridSortModel = [
                ...sortModel.map((ele) => {
                    return {
                        field: ele.field,
                        sort: ele.sort ?? 'asc',
                    };
                }),
            ];

            setCurrentSorting(sortConfig);

            if (currentPage !== 1) {
                setCurrentPage(1);
            }
        },
        [currentPage]
    );

    const updateFilters = useCallback(
        (filters: GridFilterModel) => {
            setCurrentFilters(filters);

            if (currentPage !== 1) {
                setCurrentPage(1);
            }
        },
        [currentPage]
    );

    const reset = useCallback(() => {
        setCurrentPage(config.pagination?.page ?? 1);
        setCurrentPageSize(config.pagination?.pageSize ?? 50);
        setCurrentSorting(config.sorting ?? []);
        setCurrentFilters(config.filterModel ?? { items: [] });
    }, [config.pagination?.page, config.pagination?.pageSize, config.sorting, config.filterModel]);

    const enhancedRefetch = useCallback(
        async (variables?: Record<string, unknown>) => {
            const newVariables = variables
                ? {
                      ...queryVariables,
                      ...variables,
                  }
                : queryVariables;

            return refetch(newVariables);
        },
        [refetch, queryVariables]
    );

    return {
        data: data as T[],
        loading,
        error,
        paginationMetadata,
        refetch: enhancedRefetch,
        fetchMore,
        updateSorting,
        currentSorting,
        updateFilters,
        reset,
        filterState: currentFilters,
        networkStatus: networkStatus ?? NetworkStatus.ready,
    };
}

export default usePaginatedQuery;
